// MARK://dhk8/pos/pages/FloorPage
// Plano de mesas del POS — items E + F del backlog NintaiGO.
//
//  F (reestructura visual): el plano se reorganizó. Antes el FloorEditor + dos
//  drawers (reservas/fila) llenaban los tres lados y no había código de color
//  ni leyenda. Ahora:
//    • Una barra de encabezado limpia: título + leyenda de estados + acciones.
//    • View mode usa <FloorCanvas> — un plano de sólo-lectura con estados
//      pintados por color y la info de cada mesa ordenada (código + capacidad).
//    • Reservas y Fila son paneles laterales colapsables (ocultos por default
//      para que el plano respire); en pantalla angosta caen debajo.
//    • Edit mode mantiene el <FloorEditor> completo (arrastrar, crear, etc.).
//
//  E1 (modelo de estados): cada mesa expone disponible / ocupada / reservada /
//  por limpiar (sucia) / fuera de servicio. El staff cambia el estado desde el
//  TableSheet de la mesa.
//
//  E2 (indicador de notificación): cada mesa con notificaciones PENDIENTES del
//  comensal muestra un badge con conteo; al abrir la mesa se ven y se resuelven.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Pencil, Eye, Filter, Calendar, Users } from 'lucide-react'
import {
  FloorEditor,
  type FloorEditorAddTableInput,
  type TableDto,
} from '@dockethub/ui-restaurant'
import {
  useFloorState,
  useSaveFloorTables,
  useSetTableStatus,
  useFloorNotifications,
} from '../hooks/useFloor.js'
import {
  createTable as apiCreateTable,
  deleteTable as apiDeleteTable,
  patchTable as apiPatchTable,
  createSection as apiCreateSection,
  deleteSection as apiDeleteSection,
  openTableSession,
  closeTableSession,
  seatWaitlistEntry,
  updateReservation,
  updateGuestNotificationStatus,
  type ReservationDto,
  type WaitlistEntryDto,
} from '../lib/clients/floor.js'
import { cancelOrder } from '../lib/clients/orders.js'
import { setTableStatus as apiSetTableStatus } from '../lib/clients/floor.js'
import { requestManagerPin, requireManagerAuth } from '../features/security/pin-gate-store.js'
import ReservationsDrawer from './floor/ReservationsDrawer.js'
import WaitlistPanel from './floor/WaitlistPanel.js'
import { FloorCanvas } from '../features/floor/FloorCanvas.js'
import { TableSheet, type SettableStatus } from '../features/floor/TableSheet.js'
import { MoveOrderDialog } from '../features/floor/MoveOrderDialog.js'
import { useOrderStore } from '../stores/order-store.js'
import { useAuthStore } from '../stores/auth-store.js'
import { useQueryClient } from '@tanstack/react-query'
import { floorKeys } from '../hooks/useFloor.js'
import { findOpenOrderByTableSession } from '../lib/clients/orders.js'
import { useTenantId } from '../hooks/useTenantId.js'
import { useRealtimeSubscription } from '@dockethub/realtime'
import { t } from '../i18n/es-MX.js'

const SECTION_COLORS = ['#1F8B3A', '#2D86B7', '#E0A23C', '#155F26', '#48C76C']
const POLL_MS = 30_000

/** E1 — leyenda de estados. Orden y color reflejan el modelo completo. */
const STATUS_LEGEND: { key: string; label: string }[] = [
  { key: 'available', label: 'Disponible' },
  { key: 'occupied', label: 'Ocupada' },
  { key: 'reserved', label: 'Reservada' },
  { key: 'cleaning', label: 'Por limpiar' },
  { key: 'unavailable', label: 'Fuera de servicio' },
]

export default function FloorPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data, isLoading, isError, error, refetch, isFetching } = useFloorState()
  const { data: notifByTable } = useFloorNotifications()
  const saveTables = useSaveFloorTables()
  const setStatus = useSetTableStatus()
  const [editing, setEditing] = useState(false)
  const [tables, setTables] = useState<TableDto[]>([])
  const [dirty, setDirty] = useState(false)
  // Espejo en ref: el debounce del tiempo real lo lee fuera del render.
  const dirtyRef = useRef(false)
  // refetch en un ref: su identidad cambia por render y el debounce corre fuera.
  const refetchRef = useRef<null | (() => unknown)>(null)
  useEffect(() => { dirtyRef.current = dirty }, [dirty])
  const [openingTableId, setOpeningTableId] = useState<string | null>(null)
  // Lane #164 — los drawers viejos siguen existiendo (panel lateral opcional
  // si abre desde botones del header), pero los items del sidebar POS ahora
  // apuntan a /reservations y /waitlist standalone (no más ?drawer=...).
  const [searchParams, setSearchParams] = useSearchParams()
  const [showReservations, setShowReservations] = useState(false)
  const [showWaitlist, setShowWaitlist] = useState(false)
  // Filtro por sección/zona. null = todas las mesas visibles.
  // Persistido en URL para deep-link (`/floor?section=<id>`).
  const initialSectionFilter = searchParams.get('section')
  const [sectionFilter, setSectionFilter] = useState<string | null>(initialSectionFilter)

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    if (sectionFilter) next.set('section', sectionFilter)
    else next.delete('section')
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionFilter])
  /** Mesa abierta en el TableSheet (view mode). */
  const [sheetTableId, setSheetTableId] = useState<string | null>(null)
  /** Lane #165 — orden + folio de la mesa abierta (para acción "Mover"). */
  const [sheetOrder, setSheetOrder] = useState<{ id: string; folio: string; totalCents: number; openedAt: string } | null>(null)
  /** Lane #165 — mostrar dialog "mover orden a otra mesa". */
  const [moveOrderOpen, setMoveOrderOpen] = useState(false)
  const [busyNotifId, setBusyNotifId] = useState<string | null>(null)
  /** When set, a tap on a table calls this with the table id. Used to seat a
   *  reservation or a waitlist entry: parent enters "pick a table" mode and
   *  the next tap delegates to the seating call. */
  const seatActionRef = useRef<((table: TableDto) => Promise<void>) | null>(null)
  const [pickingForLabel, setPickingForLabel] = useState<string | null>(null)
  const moved = useRef<Set<string>>(new Set())
  const setTable = useOrderStore((s) => s.setTable)
  const createOrder = useOrderStore((s) => s.createOrder)
  const user = useAuthStore((s) => s.user)
  const tenantId = useTenantId()

  // Sync local edit-state from server fetch (only when not in middle of edits).
  useEffect(() => {
    if (!data || dirty) return
    setTables(data.tables)
  }, [data, dirty])

  useEffect(() => { refetchRef.current = refetch }, [refetch])

  // Poll + refetch on focus. El sondeo queda como RESPALDO del tiempo real.
  useEffect(() => {
    const iv = setInterval(() => { if (!dirty) void refetch() }, POLL_MS)
    const onFocus = () => { if (!dirty) void refetch() }
    window.addEventListener('focus', onFocus)
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus) }
  }, [refetch, dirty])

  // Tiempo real. Antes el plano NO tenía suscripción alguna y dependía del
  // sondeo de 30s: el estado de una mesa no cambiaba hasta refrescar.
  //   `floor`  → dhk8.pos.table.* y dhk8.pos.table_session.*
  //   `orders` → abrir/cerrar/cancelar una orden también mueve la ocupación
  //
  // Patrón calcado de usePendingAcceptanceCount (el que sí reacciona en vivo):
  // tenant desde el auth-store, un handler POR canal (compartir la misma función
  // entre dos suscripciones las mete al mismo Set del WsClient), debounce para
  // ráfagas, e invalidateQueries por queryKey. El sondeo queda de respaldo.
  const rtTenantId = useAuthStore((s) => s.activeTenantId())
  const rtDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rtBump = useCallback(() => {
    if (rtDebounce.current !== null) clearTimeout(rtDebounce.current)
    rtDebounce.current = setTimeout(() => {
      if (dirtyRef.current) return
      // `refetch()` directo: no depende de que la queryKey case. La invalidacion
      // por PREFIJO ['floor'] cubre ademas las notificaciones por mesa.
      void qc.invalidateQueries({ queryKey: ['floor'] })
      void refetchRef.current?.()
    }, 300)
  }, [qc, rtTenantId])
  useEffect(() => () => { if (rtDebounce.current !== null) clearTimeout(rtDebounce.current) }, [])
  const rtFloorCb = useCallback(() => { rtBump() }, [rtBump])
  const rtOrdersCb = useCallback(() => { rtBump() }, [rtBump])
  const rtFloorCh = rtTenantId !== null && rtTenantId.length > 0 ? `t:${rtTenantId}:floor` : ''
  const rtOrdersCh = rtTenantId !== null && rtTenantId.length > 0 ? `t:${rtTenantId}:orders` : ''
  useRealtimeSubscription(rtFloorCh, rtFloorCb, [rtTenantId])
  useRealtimeSubscription(rtOrdersCh, rtOrdersCb, [rtTenantId])

  // ── E2: notification counts per table ─────────────────────────────────────
  const notificationCountByTable = useMemo(() => {
    const m = new Map<string, number>()
    if (notifByTable) for (const [tableId, list] of notifByTable) m.set(tableId, list.length)
    return m
  }, [notifByTable])

  const sheetTable = tables.find((tb) => tb.id === sheetTableId) ?? null
  const sheetNotifications = sheetTableId ? notifByTable?.get(sheetTableId) ?? [] : []
  const sheetSession = (data?.activeSessions ?? []).find((s) => s.tableId === sheetTableId)

  // Lane #165 — resolver la orden de la sesión activa para el modal info.
  // Sin esto el botón "Mover orden" no tiene orderId/folio para mostrar.
  useEffect(() => {
    if (!sheetSession || !tenantId) {
      setSheetOrder(null)
      return
    }
    let alive = true
    if (sheetSession.orderId) {
      // Tenemos el orderId; pedimos el order detail mínimo.
      void import('../lib/clients/orders.js').then(({ getOrder }) =>
        getOrder(sheetSession.orderId!).then((o) => {
          if (alive) setSheetOrder({ id: o.id, folio: o.folio, totalCents: o.totalCents, openedAt: o.openedAt })
        }).catch(() => alive && setSheetOrder(null)),
      )
    } else {
      // Sesión sin orderId — buscamos por sessionId.
      void findOpenOrderByTableSession(tenantId, sheetSession.id)
        .then((o) => {
          if (alive && o) setSheetOrder({ id: o.id, folio: o.folio, totalCents: o.totalCents, openedAt: o.openedAt })
          else if (alive) setSheetOrder(null)
        })
        .catch(() => alive && setSheetOrder(null))
    }
    return () => { alive = false }
  }, [sheetSession, tenantId])

  /** Resolve the active order for an occupied table → navigate to it (or open a new one). */
  async function goToTableOrder(table: TableDto) {
    if (openingTableId) return
    const existing = (data?.activeSessions ?? []).find((s) => s.tableId === table.id)
    if (existing) {
      setTable({
        tableCode: table.code,
        tableSessionId: existing.id,
        covers: existing.covers,
        waiterName: user?.fullName ?? null,
      })
      if (existing.orderId) {
        navigate(`/order?orderId=${encodeURIComponent(existing.orderId)}`)
        return
      }
      if (tenantId) {
        const recovered = await findOpenOrderByTableSession(tenantId, existing.id).catch(() => null)
        if (recovered) {
          navigate(`/order?orderId=${encodeURIComponent(recovered.id)}`)
          return
        }
      }
      navigate('/order')
      return
    }
    setOpeningTableId(table.id)
    try {
      const covers = table.capacity ?? 1
      const session = user
        ? await openTableSession(table.id, { covers, serverId: user.id }).catch(() => null)
        : null
      const tableSessionId = session?.id ?? null
      setTable({
        tableCode: table.code,
        tableSessionId,
        covers,
        waiterName: user?.fullName ?? null,
      })
      // Navegación OPTIMISTA: la pantalla de comanda aparece en cuanto la sesión
      // está abierta, sin esperar el SEGUNDO round-trip de grafo (createOrder).
      // El order-store puebla la orden reactivamente. Antes asignar mesa
      // bloqueaba en 2 mutaciones de grafo en serie (~2× latencia + picos).
      setOpeningTableId(null)
      navigate('/order')
      void createOrder({ covers, ...(tableSessionId ? { tableSessionId } : {}) }).catch(() => undefined)
    } catch {
      setOpeningTableId(null)
      navigate('/order')
    }
  }

  /** Tap on a table in VIEW mode. If we're seating a reservation/waitlist entry,
   *  delegate; otherwise open the TableSheet for monitoring + actions. */
  function handleSelectTable(table: TableDto) {
    if (seatActionRef.current) {
      const action = seatActionRef.current
      seatActionRef.current = null
      setPickingForLabel(null)
      void action(table)
      return
    }
    setSheetTableId(table.id)
  }

  // ── TableSheet actions ────────────────────────────────────────────────────
  async function handleChangeStatus(status: SettableStatus) {
    if (!data?.mapId || !sheetTable) return
    // optimistic — the canvas recolors immediately
    setTables((prev) => prev.map((tb) => (tb.id === sheetTable.id ? { ...tb, status } : tb)))
    try {
      await setStatus.mutateAsync({ mapId: data.mapId, tableId: sheetTable.id, status })
    } finally {
      void refetch()
    }
    setSheetTableId(null)
  }

  /**
   * Lane #172 — Force-clear: el escape hatch para liberar una mesa cuyo
   * cliente se fue sin pagar / la orden quedó huérfana. Manager-PIN + razón
   * obligatoria; al aprobar cancela la orden (si la hay) y deja la mesa
   * AVAILABLE. La razón viaja como `reason` del cancelOrder para audit.
   */
  async function handleForceClear() {
    if (!data?.mapId || !sheetTable) return
    // TCK-0016 / F2: rol-aware. owner/admin/manager (p.ej. el dueño con su login
    // 100001) procede SIN PIN — antes usábamos `requestManagerPin`, que SIEMPRE
    // exigía un manager-PIN de platform-auth que el dueño no tiene provisionado,
    // así que NUNCA podía liberar una mesa con sesión. `requireManagerAuth` deja
    // pasar a gerencia directo; a cajero/mesero le sigue pidiendo el PIN.
    const result = await requireManagerAuth('floor.forceClear', {
      requireReason: true,
      contextLabel: `Mesa ${sheetTable.code}${sheetOrder ? ` — Orden ${sheetOrder.folio}` : ''}`,
    })
    if (!result.proceed) return
    const reason = (result.reason ?? '').trim() || 'Forzado por gerente sin motivo capturado'
    try {
      // 1) Cancelar la orden activa (si existe) — sin esto los ítems quedan
      //    en estado abierto y la próxima sesión los puede arrastrar.
      if (sheetOrder?.id) {
        try {
          await cancelOrder(sheetOrder.id, `force-clear: ${reason}`)
        } catch (e) {
          // Una orden YA terminal (CANCELLED/CLOSED) NO bloquea forzar la
          // liberación: el objetivo es liberar la MESA, y una orden ya
          // cerrada/cancelada ya no la retiene. Tragamos SÓLO ese caso y
          // seguimos a cerrar sesión + AVAILABLE (cascade idempotente del
          // backend). Otros errores (p.ej. "has payments; refund first") SÍ
          // se propagan — no abandonamos una mesa con dinero sin resolver.
          const msg = e instanceof Error ? e.message : String(e)
          if (!/already (CANCELLED|CLOSED)/i.test(msg)) throw e
        }
      }
      // 2) Cerrar la sesión de mesa (libera la fila PosTableSession y
      //    derriba el OCCUPIED desde el cascade del backend).
      if (sheetSession?.id) {
        try { await closeTableSession(sheetSession.id) } catch { /* sesión ya cerrada */ }
      }
      // 3) Marcar AVAILABLE explícito como red de seguridad (idempotente
      //    con el cascade).
      await apiSetTableStatus(data.mapId, sheetTable.id, 'AVAILABLE')
      await refetch()
      setSheetTableId(null)
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`No se pudo forzar la liberación: ${e instanceof Error ? e.message : 'error'}`)
    }
  }

  async function handleResolveNotification(id: string) {
    setBusyNotifId(id)
    try {
      await updateGuestNotificationStatus(id, 'RESOLVED')
      await qc.invalidateQueries({ queryKey: floorKeys.notifications(tenantId) })
    } finally {
      setBusyNotifId(null)
    }
  }

  function handleTablesChange(next: TableDto[]) {
    const prev = new Map(tables.map((tb) => [tb.id, tb]))
    next.forEach((n) => {
      const p = prev.get(n.id)
      if (p && (p.posX !== n.posX || p.posY !== n.posY)) moved.current.add(n.id)
    })
    setTables(next)
    setDirty(true)
  }

  async function handleSave() {
    if (!data?.mapId) return
    const toSave = tables.filter((tb) => moved.current.has(tb.id))
    if (toSave.length === 0) { setDirty(false); return }
    await saveTables.mutateAsync({ mapId: data.mapId, tables: toSave })
    moved.current.clear()
    setDirty(false)
  }

  async function handleAddTable(input: FloorEditorAddTableInput) {
    if (!data?.mapId) return
    await apiCreateTable(data.mapId, {
      code: input.code, capacity: input.capacity,
      posX: 40, posY: 40,
      widthPx: input.shape === 'ROUND' ? 80 : 120,
      heightPx: input.shape === 'ROUND' ? 80 : 90,
      shape: input.shape, sectionId: input.sectionId,
    })
    await refetch()
  }
  async function handleDeleteTable(id: string) {
    if (!data?.mapId) return
    // Lane #172 — eliminar una mesa es destructivo (rompe historial de
    // sesiones huérfanas); pide PIN de gerente. NO requiere razón porque
    // el editor del floor es un contexto reflexivo (manager está editando
    // layout, no operando turno).
    const { approved } = await requestManagerPin('floor.deleteTable')
    if (!approved) return
    await apiDeleteTable(data.mapId, id)
    await refetch()
  }
  async function handleUpdateTable(id: string, updates: Partial<TableDto> & { sectionId?: string }) {
    if (!data?.mapId) return
    const body: Parameters<typeof apiPatchTable>[2] = {}
    if (updates.code) body.code = updates.code
    if (typeof updates.capacity === 'number') body.capacity = updates.capacity
    if (updates.sectionId) body.sectionId = updates.sectionId
    if (updates.shape) body.shape = updates.shape
    await apiPatchTable(data.mapId, id, body)
    await refetch()
  }
  async function handleAddSection(name: string) {
    if (!data?.mapId) return
    await apiCreateSection(data.mapId, name)
    await refetch()
  }
  async function handleDeleteSection(id: string) {
    if (!data?.mapId) return
    await apiDeleteSection(data.mapId, id)
    await refetch()
  }

  /** Reservation marked "Sentar" → pick a table, then open session for the
   *  reservation's party and tie the reservation to that session. */
  async function handleSeatReservation(r: ReservationDto) {
    if (!user) { alert('Falta sesión de mesero.'); return }
    setPickingForLabel(`Elige mesa para ${r.name} (reserva)`)
    seatActionRef.current = async (table) => {
      const covers = r.partySize
      const session = await openTableSession(table.id, { covers, serverId: user.id }).catch(() => null)
      const tableSessionId = session?.id ?? null
      if (tableSessionId) {
        await updateReservation(r.id, { status: 'SEATED', tableId: table.id }).catch(() => undefined)
      }
      setTable({ tableCode: table.code, tableSessionId, covers, waiterName: user.fullName ?? null })
      await createOrder({ covers, ...(tableSessionId ? { tableSessionId } : {}) }).catch(() => undefined)
      await refetch()
      navigate('/order')
    }
  }

  /** Waitlist entry marked "Sentar" → pick a table, then call seatWaitlistEntry. */
  async function handleSeatWaitlist(entry: WaitlistEntryDto) {
    if (!user) { alert('Falta sesión de mesero.'); return }
    setPickingForLabel(`Elige mesa para ${entry.name} (fila)`)
    seatActionRef.current = async (table) => {
      try {
        await seatWaitlistEntry(entry.id, { tableId: table.id, serverId: user.id, covers: entry.partySize })
      } catch { /* UI will refetch and surface state */ }
      setTable({ tableCode: table.code, tableSessionId: null, covers: entry.partySize, waiterName: user.fullName ?? null })
      await refetch()
      navigate('/order')
    }
  }

  function cancelPicking() {
    seatActionRef.current = null
    setPickingForLabel(null)
  }

  const totalPendingNotifs = useMemo(() => {
    let n = 0
    for (const c of notificationCountByTable.values()) n += c
    return n
  }, [notificationCountByTable])

  if (isLoading) return <CenteredNote>{t('common.loading')}</CenteredNote>
  if (isError || !data) {
    return (
      <CenteredNote tone="error">
        {t('floor.loadError')}{error instanceof Error ? `: ${error.message}` : ''}
        <button type="button" className="dhk8-pos-floor__retry" onClick={() => void refetch()}>
          {t('common.retry')}
        </button>
        <style>{noteStyles}</style>
      </CenteredNote>
    )
  }

  const sectionColorById = new Map(
    data.sections.map((s, i) => [s.id, SECTION_COLORS[i % SECTION_COLORS.length] ?? '#7E8C82']),
  )
  const canvasSectionColor = (table: TableDto): string | undefined =>
    table.sectionId ? sectionColorById.get(table.sectionId) : undefined

  // Lane #163-bis — filtro por sección. Si hay sectionFilter activo, solo
  // pintamos las mesas de esa sección; el conteo va al header. Si no hay
  // sectionFilter, todas las mesas visibles. Edit mode SIEMPRE muestra todo
  // (no se puede editar layout filtrado, es confuso).
  // Caso especial: sectionFilter='__none__' = mesas sin sección asignada.
  const visibleTables = !editing && sectionFilter
    ? sectionFilter === '__none__'
      ? tables.filter((tb) => !tb.sectionId)
      : tables.filter((tb) => tb.sectionId === sectionFilter)
    : tables
  const sectionTableCount = (sectionId: string): number =>
    tables.filter((tb) => tb.sectionId === sectionId).length
  const unassignedCount = tables.filter((tb) => !tb.sectionId).length

  return (
    <div className="dhk8-pos-floor">
      {/* ── Encabezado ──────────────────────────────────────────────────── */}
      <header className="dhk8-pos-floor__bar">
        <div className="dhk8-pos-floor__title-wrap">
          <h1 className="dhk8-pos-floor__h1">{t('floor.title')}</h1>
          {totalPendingNotifs > 0 && (
            <span className="dhk8-pos-floor__notif-total" title="Avisos del comensal pendientes">
              {totalPendingNotifs} aviso{totalPendingNotifs === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <div className="dhk8-pos-floor__bar-actions">
          <button
            type="button"
            className={`dhk8-pos-floor__btn${showReservations ? ' is-active' : ''}`}
            onClick={() => setShowReservations((v) => !v)}
            title="Mostrar / ocultar reservas"
          >
            <Calendar size={14} /> Reservas
          </button>
          <button
            type="button"
            className={`dhk8-pos-floor__btn${showWaitlist ? ' is-active' : ''}`}
            onClick={() => setShowWaitlist((v) => !v)}
            title="Mostrar / ocultar fila de espera"
          >
            <Users size={14} /> Fila
          </button>
          <button
            type="button"
            className="dhk8-pos-floor__btn dhk8-pos-floor__btn--toggle"
            onClick={() => { setDirty(false); moved.current.clear(); setSheetTableId(null); setEditing((v) => !v) }}
            disabled={!data.mapId}
            title={data.mapId ? undefined : 'No hay plano configurado'}
          >
            {editing ? <><Eye size={14} /> Ver</> : <><Pencil size={14} /> Editar</>}
          </button>
        </div>
      </header>

      {/* ── Leyenda de estados (E1) ─────────────────────────────────────── */}
      {!editing && (
        <div className="dhk8-pos-floor__legend" aria-label="Leyenda de estados de mesa">
          {STATUS_LEGEND.map((s) => (
            <span key={s.key} className="dhk8-pos-floor__legend-item">
              <span className={`dhk8-pos-floor__legend-dot dhk8-pos-floor__legend-dot--${s.key}`} aria-hidden="true" />
              {s.label}
            </span>
          ))}
        </div>
      )}

      {/* ── Filtro por sección/zona (Lane #163-bis) ────────────────────── */}
      {!editing && data.sections.length > 0 && (
        <div className="dhk8-pos-floor__zones" aria-label="Filtrar por zona">
          <span className="dhk8-pos-floor__zones-head">
            <Filter size={12} /> Zona
          </span>
          <button
            type="button"
            className={`dhk8-pos-floor__zone-chip${sectionFilter === null ? ' is-active' : ''}`}
            onClick={() => setSectionFilter(null)}
          >
            Todas <span className="dhk8-pos-floor__zone-count">{tables.length}</span>
          </button>
          {data.sections.map((s, i) => {
            const color = SECTION_COLORS[i % SECTION_COLORS.length] ?? '#7E8C82'
            const count = sectionTableCount(s.id)
            const isActive = sectionFilter === s.id
            return (
              <button
                type="button"
                key={s.id}
                className={`dhk8-pos-floor__zone-chip${isActive ? ' is-active' : ''}`}
                onClick={() => setSectionFilter(isActive ? null : s.id)}
                style={{ ['--dhk8-zone-color' as string]: color }}
                title={`${count} mesa${count !== 1 ? 's' : ''}`}
              >
                <span className="dhk8-pos-floor__zone-dot" aria-hidden="true" />
                {s.name}
                <span className="dhk8-pos-floor__zone-count">{count}</span>
              </button>
            )
          })}
          {unassignedCount > 0 && (
            <button
              type="button"
              className={`dhk8-pos-floor__zone-chip dhk8-pos-floor__zone-chip--mute${sectionFilter === '__none__' ? ' is-active' : ''}`}
              onClick={() => setSectionFilter(sectionFilter === '__none__' ? null : '__none__')}
              title="Mesas sin sección asignada"
            >
              Sin zona <span className="dhk8-pos-floor__zone-count">{unassignedCount}</span>
            </button>
          )}
        </div>
      )}

      {pickingForLabel && (
        <div className="dhk8-pos-floor__picking">
          <span>{pickingForLabel}</span>
          <button type="button" className="dhk8-pos-floor__btn" onClick={cancelPicking}>
            Cancelar
          </button>
        </div>
      )}

      {/* ── Cuerpo ──────────────────────────────────────────────────────── */}
      <div className={`dhk8-pos-floor__layout${(showReservations || showWaitlist) && !editing ? ' has-aside' : ''}`}>
        <div className="dhk8-pos-floor__center">
          {tables.length === 0 && !editing ? (
            <CenteredNote>No hay mesas configuradas todavía.</CenteredNote>
          ) : editing ? (
            <FloorEditor
              tables={tables}
              sections={data.sections}
              floorWidthPx={data.plan?.widthPx ?? 920}
              floorHeightPx={data.plan?.heightPx ?? 640}
              editing
              sectionColorByIndex={SECTION_COLORS}
              tableSectionId={(id) => tables.find((tb) => tb.id === id)?.sectionId}
              onTablesChange={handleTablesChange}
              onSave={handleSave}
              dirty={dirty}
              saving={saveTables.isPending}
              onAddTable={handleAddTable}
              onDeleteTable={handleDeleteTable}
              onUpdateTable={handleUpdateTable}
              onAddSection={handleAddSection}
              onDeleteSection={handleDeleteSection}
              onRefresh={() => void refetch()}
              onSelectTable={handleSelectTable}
              title={t('floor.title')}
            />
          ) : (
            <FloorCanvas
              tables={visibleTables}
              floorWidthPx={data.plan?.widthPx ?? 920}
              floorHeightPx={data.plan?.heightPx ?? 640}
              notificationCountByTable={notificationCountByTable}
              sectionColorFor={canvasSectionColor}
              onSelectTable={handleSelectTable}
              emphasized={sectionFilter !== null}
            />
          )}
        </div>

        {(showReservations || showWaitlist) && !editing && (
          <aside className="dhk8-pos-floor__aside">
            {showReservations && <ReservationsDrawer onSeatReservation={handleSeatReservation} />}
            {showWaitlist && <WaitlistPanel onSeatEntry={handleSeatWaitlist} />}
          </aside>
        )}
      </div>

      {isFetching && <div className="dhk8-pos-floor__hint">Sincronizando…</div>}

      {/* ── TableSheet (view mode) ──────────────────────────────────────── */}
      {sheetTable && !editing && (
        <TableSheet
          table={sheetTable}
          editing={false}
          notifications={sheetNotifications}
          onResolveNotification={(id) => void handleResolveNotification(id)}
          busyNotificationId={busyNotifId}
          {...(sheetSession ? { sessionOpenedAt: sheetSession.openedAt } : {})}
          {...(sheetSession ? { sessionCovers: sheetSession.covers } : {})}
          {...(sheetSession?.joinCode ? { sessionJoinCode: sheetSession.joinCode } : {})}
          {...(sheetSession?.orderId ? { sessionOrderId: sheetSession.orderId } : {})}
          {...(sheetOrder ? { order: sheetOrder } : {})}
          onClose={() => setSheetTableId(null)}
          onChangeStatus={(s) => void handleChangeStatus(s)}
          onOpenOrder={() => { const tb = sheetTable; setSheetTableId(null); void goToTableOrder(tb) }}
          onViewOrder={() => { const tb = sheetTable; setSheetTableId(null); void goToTableOrder(tb) }}
          onMoveOrder={() => setMoveOrderOpen(true)}
          onForceClear={() => void handleForceClear()}
        />
      )}

      {/* Lane #165 — Mover comanda a otra mesa */}
      {moveOrderOpen && sheetTable && sheetOrder && (
        <MoveOrderDialog
          fromTable={sheetTable}
          orderId={sheetOrder.id}
          orderFolio={sheetOrder.folio}
          totalCents={sheetOrder.totalCents}
          allTables={tables}
          occupiedTableIds={new Set((data?.activeSessions ?? []).map((s) => s.tableId))}
          onClose={() => setMoveOrderOpen(false)}
          onMoved={() => {
            setMoveOrderOpen(false)
            setSheetTableId(null)
            void refetch()
          }}
        />
      )}

      <style>{floorPageStyles}</style>
    </div>
  )
}

function CenteredNote({ children, tone }: { children: React.ReactNode; tone?: 'error' }) {
  return (
    <div className={`dhk8-pos-floor__note${tone === 'error' ? ' dhk8-pos-floor__note--error' : ''}`}>
      {children}
      <style>{noteStyles}</style>
    </div>
  )
}

const noteStyles = `
  .dhk8-pos-floor__note {
    min-height: 50dvh;
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px;
    font-family: var(--font-mono, monospace); font-size: var(--pos-text-sm, .875rem);
    color: var(--pos-fg-tertiary, #7E8C82); text-align: center; padding: 24px;
  }
  .dhk8-pos-floor__note--error { color: var(--pos-danger, #ef4444); }
  .dhk8-pos-floor__retry {
    padding: 6px 14px; cursor: pointer; font: inherit;
    background: transparent; color: inherit; border: 1px solid currentColor;
  }
`

const floorPageStyles = `
  /* NintaiGO rule: sin border-radius, sin box-shadow. Única excepción:
     dots de estado en la leyenda (.dhk8-pos-floor__legend-dot). */
  .dhk8-pos-floor { display: flex; flex-direction: column; gap: var(--pos-space-3, 12px); padding: var(--pos-space-4, 16px); }

  /* ── Encabezado ─────────────────────────────────────────────────────── */
  .dhk8-pos-floor__bar { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
  .dhk8-pos-floor__title-wrap { display: flex; align-items: center; gap: 10px; }
  .dhk8-pos-floor__h1 {
    margin: 0; font-family: var(--font-display, var(--font-sans, sans-serif));
    font-size: var(--pos-text-2xl, 24px); font-weight: var(--pos-weight-semibold, 600);
    color: var(--pos-fg, #E8EFE9);
  }
  /* Total-avisos chip — rectangular (no pill), inverted bg/fg para destacar
     sin necesidad de shadow. */
  .dhk8-pos-floor__notif-total {
    background: var(--pos-warning, #E0A23C); color: #1a1205;
    font-size: var(--pos-text-xs, 12px); font-weight: 800;
    padding: 3px 10px;
  }
  .dhk8-pos-floor__bar-actions { display: flex; gap: 8px; flex-wrap: wrap; }
  .dhk8-pos-floor__btn {
    display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; cursor: pointer; font: inherit;
    font-size: var(--pos-text-sm, 13px); font-weight: var(--pos-weight-medium, 500);
    background: var(--pos-surface, #161C18); color: var(--pos-fg-secondary, #B6BFB8);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    min-height: 40px;
  }
  .dhk8-pos-floor__btn:hover:not(:disabled) { color: var(--pos-fg, #E8EFE9); border-color: var(--pos-tint, #1F8B3A); }
  .dhk8-pos-floor__btn:disabled { opacity: .5; cursor: default; }
  .dhk8-pos-floor__btn.is-active { color: var(--pos-tint-on, #48C76C); border-color: var(--pos-tint, #1F8B3A); background: var(--pos-tint-soft, rgba(31,139,58,.12)); }
  .dhk8-pos-floor__btn--toggle { background: var(--pos-elevated, #1E2520); }

  /* ── Leyenda de estados ─────────────────────────────────────────────── */
  .dhk8-pos-floor__legend {
    display: flex; flex-wrap: wrap; gap: 6px 16px;
    padding: 8px 12px;
    background: var(--pos-surface, #161C18); border: 1px solid var(--pos-separator, rgba(255,255,255,.08));
  }
  .dhk8-pos-floor__legend-item {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-secondary, #B6BFB8);
  }
  /* DOT — única excepción autorizada al rule de no-radius. */
  .dhk8-pos-floor__legend-dot { width: 11px; height: 11px; border-radius: var(--radius-dot); border: 2px solid; flex: 0 0 auto; }
  .dhk8-pos-floor__legend-dot--available  { border-color: var(--pos-success, #2EA043); background: color-mix(in oklch, var(--pos-success, #2EA043) 30%, transparent); }
  .dhk8-pos-floor__legend-dot--occupied   { border-color: var(--pos-danger, #E5484D);  background: color-mix(in oklch, var(--pos-danger, #E5484D) 30%, transparent); }
  .dhk8-pos-floor__legend-dot--reserved   { border-color: var(--pos-warning, #E0A23C); background: color-mix(in oklch, var(--pos-warning, #E0A23C) 30%, transparent); }
  .dhk8-pos-floor__legend-dot--cleaning   { border-color: var(--pos-info, #4493F8);    background: color-mix(in oklch, var(--pos-info, #4493F8) 30%, transparent); }
  .dhk8-pos-floor__legend-dot--unavailable { border-color: var(--pos-fg-tertiary, #7E8C82); border-style: dashed; background: transparent; }

  /* ── Lane #163-bis — filtro de zonas ──────────────────────────────── */
  .dhk8-pos-floor__zones {
    display: flex; flex-wrap: wrap; align-items: center; gap: 6px;
    padding: 6px 0;
  }
  .dhk8-pos-floor__zones-head {
    display: inline-flex; align-items: center; gap: 4px;
    font-family: var(--font-mono, ui-monospace); font-size: 10px;
    letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--pos-fg-tertiary, #7E8C82); padding-right: 4px;
  }
  .dhk8-pos-floor__zone-chip {
    display: inline-flex; align-items: center; gap: 6px;
    min-height: 32px; padding: 4px 10px;
    background: transparent;
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,0.14));
    color: var(--pos-fg-secondary, #B6BFB8);
    font-family: inherit; font-size: 12px; cursor: pointer;
    transition: background 120ms ease, border-color 120ms ease;
  }
  .dhk8-pos-floor__zone-chip:hover { background: var(--pos-elevated, #1E2520); }
  .dhk8-pos-floor__zone-chip.is-active {
    background: color-mix(in oklch, var(--dhk8-zone-color, var(--pos-tint, #1F8B3A)) 20%, transparent);
    border-color: var(--dhk8-zone-color, var(--pos-tint, #1F8B3A));
    color: var(--pos-fg, #E8EFE9);
    font-weight: var(--pos-weight-semibold, 600);
  }
  .dhk8-pos-floor__zone-chip--mute { color: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-pos-floor__zone-dot {
    width: 9px; height: 9px; border-radius: 50%;
    background: var(--dhk8-zone-color, var(--pos-tint, #1F8B3A));
    flex: 0 0 auto;
  }
  .dhk8-pos-floor__zone-count {
    display: inline-block; min-width: 18px; padding: 0 5px;
    font-family: var(--font-mono, ui-monospace); font-size: 10px;
    font-weight: var(--pos-weight-semibold, 600);
    background: var(--pos-surface, #161C18);
    color: var(--pos-fg-tertiary, #7E8C82);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,0.14));
    text-align: center; line-height: 16px;
  }
  .dhk8-pos-floor__zone-chip.is-active .dhk8-pos-floor__zone-count {
    color: var(--pos-fg, #E8EFE9);
    border-color: var(--dhk8-zone-color, var(--pos-tint, #1F8B3A));
  }

  /* ── Picking banner ─────────────────────────────────────────────────── */
  .dhk8-pos-floor__picking {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    padding: 8px 12px; background: var(--pos-tint-soft, rgba(31,139,58,.12)); color: var(--pos-tint-on, #48C76C);
    border: 1px solid var(--pos-tint, #1F8B3A);
    font-size: 13px;
  }

  /* ── Cuerpo ─────────────────────────────────────────────────────────── */
  .dhk8-pos-floor__layout { display: grid; grid-template-columns: 1fr; gap: var(--pos-space-3, 12px); align-items: start; }
  .dhk8-pos-floor__layout.has-aside { grid-template-columns: minmax(0, 1fr) 320px; }
  .dhk8-pos-floor__center { min-width: 0; }
  .dhk8-pos-floor__aside { display: flex; flex-direction: column; gap: var(--pos-space-3, 12px); min-width: 0; }
  .dhk8-pos-floor__hint { font-size: 11px; color: var(--pos-fg-tertiary, #7E8C82); text-align: right; }

  @media (max-width: 900px) {
    .dhk8-pos-floor__layout.has-aside { grid-template-columns: 1fr; }
  }
`
